import { DeliveryPointSegment, DeliveryPointType } from '../types/delivery-point.type';
import { DeliveryPointDetailsAddress } from '../types/delivery-point-details.type';
import { CreateDeliveryPointAddressDTO, CreateDeliveryPointRequestDTO } from '../types/dtos/create-delivery-point.dto';
import {
  isBoundedTextInvalid,
  isCompanyCodeInvalid,
  isCompanyType,
  isEmailInvalid,
  isStructuredAddressComplete,
} from './delivery-point-form-rules.util';

export type CreateDeliveryPointFormValues = {
  name: string;
  type: DeliveryPointType | '';
  segment: DeliveryPointSegment | null;
  address: DeliveryPointDetailsAddress;
  phone: string;
  email: string;
  website: string;
  code: string;
  discount: string;
  isActive: boolean;
};

export type CreateDeliveryPointFormErrors = {
  isNameInvalid: boolean;
  isEmailInvalid: boolean;
  isCodeInvalid: boolean;
  isDiscountInvalid: boolean;
};

const NAME_MIN_LENGTH = 2;
const NAME_MAX_LENGTH = 100;
const PHONE_NUMBER_LENGTH = 10;
const MAX_DISCOUNT = 100;

export const NAME_ERROR = `Entre ${NAME_MIN_LENGTH} et ${NAME_MAX_LENGTH} caractères`;
export const DISCOUNT_ERROR = `Entre 0 et ${MAX_DISCOUNT}`;
export const EMAIL_ERROR = 'Adresse email invalide';

export const EMPTY_CREATE_DELIVERY_POINT_FORM_VALUES: CreateDeliveryPointFormValues = {
  name: '',
  type: '',
  segment: null,
  address: { street: '', zipCode: '', city: '', country: '', informations: '', comment: '' },
  phone: '',
  email: '',
  website: '',
  code: '',
  discount: '',
  isActive: true,
};

const isLengthOutside = (value: string, min: number, max: number): boolean =>
  value.trim().length < min || value.trim().length > max;

const isDiscountOutOfRange = (discount: string): boolean => {
  if (discount === '') return false;

  const value = Number(discount);
  return Number.isNaN(value) || value < 0 || value > MAX_DISCOUNT;
};

export const getCreateDeliveryPointFormErrors = (
  values: CreateDeliveryPointFormValues
): CreateDeliveryPointFormErrors => ({
  isNameInvalid: isLengthOutside(values.name, NAME_MIN_LENGTH, NAME_MAX_LENGTH),
  isEmailInvalid: isEmailInvalid(values.email),
  isCodeInvalid: isCompanyType(values.type) && isCompanyCodeInvalid(values.code),
  isDiscountInvalid: isCompanyType(values.type) && isDiscountOutOfRange(values.discount),
});

export const isCreateDeliveryPointFormValid = (values: CreateDeliveryPointFormValues): boolean => {
  const errors = getCreateDeliveryPointFormErrors(values);
  const hasError = Object.values(errors).some(Boolean);
  const isAddressValid =
    isStructuredAddressComplete(values.address) &&
    !isBoundedTextInvalid(values.address.informations ?? '') &&
    !isBoundedTextInvalid(values.address.comment ?? '');

  return (
    !hasError &&
    values.type !== '' &&
    values.segment !== null &&
    values.phone.length === PHONE_NUMBER_LENGTH &&
    isAddressValid
  );
};

const buildAddress = (address: DeliveryPointDetailsAddress): CreateDeliveryPointAddressDTO => {
  const country = address.country?.trim();
  const informations = address.informations?.trim();
  const comment = address.comment?.trim();

  return {
    street: address.street.trim(),
    zipCode: address.zipCode,
    city: address.city.trim(),
    ...(country ? { country } : {}),
    ...(informations ? { informations } : {}),
    ...(comment ? { comment } : {}),
  };
};

export const buildCreateDeliveryPointPayload = (
  values: CreateDeliveryPointFormValues,
  type: DeliveryPointType,
  segmentId: string
): CreateDeliveryPointRequestDTO => {
  const isCompany = isCompanyType(type);
  const email = values.email.trim();
  const website = values.website.trim();
  const code = values.code.trim();

  return {
    name: values.name.trim(),
    type,
    segmentId,
    address: buildAddress(values.address),
    phone: values.phone,
    ...(email ? { email } : {}),
    ...(website ? { website } : {}),
    ...(isCompany && code ? { code } : {}),
    ...(isCompany && values.discount !== ''
      ? { discount: { value: Number(values.discount), reduction: 'percent' as const } }
      : {}),
    isActive: values.isActive,
  };
};
